// istanbul ignore file

// externals
import type { PropsWithChildren } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider as ReduxProvider } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import type { Store } from 'redux';

// libraries
import CxThemeProvider from '@cx/ui/CxThemeProvider';
import { setupAuthNetworkIntercepts } from '@makemydeal/dr-shared-network';
import { getStoreConfigurer, historyInstance, subscribeDealerIdToSession } from '@makemydeal/dr-shared-store';
import { dashScopeCreationFactory } from '@makemydeal/dr-dash-store/utils/scopeCreationFactory';
import { ScopedStateProvider } from '@makemydeal/shared-scoped-store';
import { AuthManager, ReadyContent } from '@makemydeal/dr-shared-components';
import {
    MANAGER_VIEW_APP_PREFIX,
    offerSessionUtils,
    enableSessionIdHelper,
    newRelicLocalDevHelper
} from '@makemydeal/dr-shared-ui-utils';
import { LoadingPage, registerDealSummarySelectorTelemetry } from '@makemydeal/dr-dash-components';
import { InterstateThemeProvider } from '@interstate/components/InterstateThemeProvider';
import { interstateThemeRegistry } from '@interstate/components/InterstateTheming/themes/interstateThemeRegistry';

// styles
import { GlobalStyle } from './globalStyle/GlobalStyle.style';
import { StyledApp } from './dash.style';

// components
import { RoutingWrapper } from './shells/main/routing/routingWrapper';
import { Bootstrapper } from './shells/main/components/bootstrapper/Bootstrapper';

// utils
import { getInitialMiddleware, getTelemetryMiddleware } from './shells/main/store/initialMiddleware';
import { getRemainingMiddleware } from './shells/main/store/remainingMiddleware';
import { registerDashScreenResolver } from './shells/main/store/screenTelemetryResolver';
import { registerDashOfferTypeResolver } from './shells/main/store/offerTypeTelemetryResolver';
import {
    registerDashBreakdownContextResolver,
    registerDashLeaseBreakdownContextResolver,
    registerDashCashBreakdownContextResolver
} from './shells/main/store/breakdownContextResolver';

import { globalInitialReducers, scopedInitialReducers } from './shells/main/reducers/initialReducers';
import { getAuthConfig } from './auth';
// we don't have an initial state - it's provided by the init response
const state = {};
const allMiddleware = getInitialMiddleware().concat(getRemainingMiddleware());

// This call will result in fetch and axios network calls being intercept to inject auth headers:
setupAuthNetworkIntercepts(MANAGER_VIEW_APP_PREFIX);

// Enable New Relic session ID helper for development
enableSessionIdHelper();

// Enable New Relic local dev helpers (adds window.enableNewRelicForLocal / window.disableNewRelicForLocal)
newRelicLocalDevHelper.enableLocalDevHelpers();

// Register Deal Summary selectors with the selector-delta telemetry middleware
// (Rally US2040919). Runs unconditionally at module load — the middleware
// itself is gated by the `enableStateSnapshotTelemetry` feature toggle, so having
// an entry in the registry is a no-op when telemetry is off. Kept here (top
// of module) so that even the earliest dispatches after store creation have
// the full selector catalogue available.
registerDealSummarySelectorTelemetry();

// Register the read-side telemetry `screen` axis (US2056179). Maps the current
// route to a friendly label so base-dashboard SelectorReads (which have no
// Interstate titled-container ancestor) still roll up under a recognizable
// group. Gated downstream by `enableReduxTelemetry`, so registering is a no-op
// when telemetry is off.
registerDashScreenResolver();
registerDashOfferTypeResolver();
registerDashBreakdownContextResolver();
registerDashLeaseBreakdownContextResolver();
registerDashCashBreakdownContextResolver();

const container = document.getElementById('root');

const includeCx = !offerSessionUtils.hasDashCssOverrideValue('no-cx');

const BaseCxComponent: React.FC<PropsWithChildren> = (props) =>
    includeCx ? <CxThemeProvider themeName={'Cx'}>{props.children}</CxThemeProvider> : <>{props.children}</>;

const startReactApp = (store: Store) => {
    createRoot(container!).render(
        <>
            <GlobalStyle />

            <BaseCxComponent>
                <InterstateThemeProvider
                    themeName="Interstate"
                    applicationName="Manager View"
                    scopeName="Manager View"
                    themeRegistries={[interstateThemeRegistry]}
                >
                    <ReduxProvider store={store}>
                        <ScopedStateProvider primary>
                            <ConnectedRouter history={historyInstance}>
                                <AuthManager
                                    appPrefix={MANAGER_VIEW_APP_PREFIX}
                                    offerReadyContent={(props) => <ReadyContent {...props} loadingPage={LoadingPage} />}
                                    getConfig={getAuthConfig}
                                >
                                    <Bootstrapper>
                                        <StyledApp>
                                            <RoutingWrapper />
                                        </StyledApp>
                                    </Bootstrapper>
                                </AuthManager>
                            </ConnectedRouter>
                        </ScopedStateProvider>
                    </ReduxProvider>
                </InterstateThemeProvider>
            </BaseCxComponent>
        </>
    );
};

const startApp = async () => {
    const storeConfigurer = getStoreConfigurer(MANAGER_VIEW_APP_PREFIX);
    const store = storeConfigurer(
        state,
        allMiddleware,
        globalInitialReducers,
        scopedInitialReducers,
        true,
        dashScopeCreationFactory,
        getTelemetryMiddleware()
    );

    subscribeDealerIdToSession(store);

    startReactApp(store);
};

startApp();
